import React, { FC, useMemo, useState } from 'react';
import cx from 'classnames';
import { Field, Form, Formik } from 'formik';
import moment from 'moment-mini';

import { API } from 'core/API';

import DialogStyled from 'shared/components/Dialog';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import ButtonOutlined from 'shared/components/Buttons/ButtonOutlined';
import InlineDatePicker from 'shared/components/Pickers/InlineDatePicker';
import GoogleApiAutoCompleat from 'shared/components/GoogleApiAutoComplete';
import MediaInput from 'shared/components/MediaInput';
import Editor from 'shared/components/Editor';
import TextArea from 'shared/inputs/TextArea';
import Loader from 'shared/components/Loader';
import InputErrorHint from 'shared/components/InputErrorHint';

import { IGetLocationIdByAddressRequest } from '@joc/api-gateway';
import { PostsResponse, UpdatePostRequest } from '@joc/api-gateway/lib/api-client';
import { ReactComponent as CloseIcon } from 'assets/icons/calendar/close.svg';

import styles from './PostUpsertPopup.module.scss';
import { mapPostImageToPayload, normalizePostImageForPreview } from './postImage';
import { POST_TITLE_MAX_LENGTH, postUpsertValidationSchema } from './helpers';

type PostUpsertValues = {
	title: string;
	description: string;
	expirationDate: Date | null;
	address?: IGetLocationIdByAddressRequest;
	imagePath?: string | File;
	link?: string;
	isVirtual: boolean;
	isPublic: boolean;
};

type PostUpsertPopupProps = {
	post: PostsResponse;
	onClose: () => void;
	onSaved: () => void | Promise<void>;
};

const PostUpsertPopup: FC<PostUpsertPopupProps> = ({ post, onClose, onSaved }) => {
	const initialValues = useMemo<PostUpsertValues>(
		() => ({
			title: post?.name || '',
			description: post?.description || '',
			expirationDate: post?.expirationDate ? new Date(post.expirationDate) : null,
			address: post?.address
				? {
						countryName: post.address.countryName,
						stateName: post.address.stateName,
						cityName: post.address.cityName,
						streetName: post.address.streetName,
						buildingNumber: post.address.buildingNumber,
						zipCode: post.address.zipCode,
						pobox: post.address.pobox,
						longitude: post.address.longitude,
						latitude: post.address.latitude,
				  }
				: undefined,
			imagePath: normalizePostImageForPreview(post?.imagePath),
			link: post?.link || '',
			isVirtual: !!post?.isVirtual,
			isPublic: !!post?.isPublic,
		}),
		[post]
	);

	const [isSaving, setIsSaving] = useState(false);

	const handleSubmit = async (values: PostUpsertValues) => {
		try {
			setIsSaving(true);
			let imagePathToSend: string | undefined;
			if (values.imagePath instanceof File) {
				const response = await API.uploadPhoto({ data: values.imagePath, fileName: values.imagePath.name });
				imagePathToSend = response.fileName;
			} else if (typeof values.imagePath === 'string') {
				imagePathToSend = mapPostImageToPayload(values.imagePath);
			}
			const body = UpdatePostRequest.fromJS({
				name: values.title.trim(),
				description: values.description,
				endDate: values.expirationDate ? moment(values.expirationDate).endOf('day').toDate() : undefined,
				link: values.link || undefined,
				isVirtual: values.isVirtual,
				isPublic: values.isPublic,
				imagePath: imagePathToSend,
				address: values.isVirtual ? undefined : values.address,
			});
			await API.updatePost(post.id, undefined, body);
			await Promise.resolve(onSaved());
			onClose();
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<DialogStyled open={true} maxWidth="xl" isFullScreen={true}>
			<section className={styles.popup}>
				<div className={styles.header}>
					<span className={styles.popupTitle}>Edit Post</span>
					<CloseIcon className={styles.closeIcon} onClick={onClose} />
				</div>
				<Formik
					initialValues={initialValues}
					enableReinitialize={true}
					validationSchema={postUpsertValidationSchema}
					onSubmit={handleSubmit}
				>
					{({ values, errors, touched, submitCount, setFieldValue, setFieldTouched }) => (
						<Form className={styles.form}>
							<div className={styles.grid}>
								<div className={styles.headRow}>
									<div className={styles.mediaCol}>
										<label className={styles.label}>Main Photo</label>
										<Field
											name="imagePath"
											component={MediaInput}
											maxMegabyteSize={100}
											onlyPhotos={true}
											parentClassName={styles.mediaField}
										/>
									</div>
									<div className={styles.titleCol}>
										<div className={styles.opportunityForm_content__field}>
											<Field name="title">
												{(props: any) => {
													const titleLength = (props.field.value || '').trim().length;
													const counterClassName = cx(
														styles.opportunityForm_content__field_limit,
														{
															[styles.opportunityForm_content__field_limit_error]:
																titleLength > POST_TITLE_MAX_LENGTH,
														}
													);

													return (
														<>
															<TextArea
																label="Post name"
																placeholder="Enter post name"
																parentClassName={styles.opportunityForm__head_name}
																isRequired={true}
																isSmallHeight={true}
																isDisabledMargin={true}
																isDisableHeight={true}
																isPencilHidden={true}
																{...props}
															/>
															{!props.meta.error || !props.meta.touched ? (
																<span className={counterClassName}>
																	Maximum characters:
																	{` ${titleLength}/${POST_TITLE_MAX_LENGTH}`}
																</span>
															) : null}
														</>
													);
												}}
											</Field>
										</div>
										{(touched.title || submitCount > 0) && errors.title ? (
											<InputErrorHint errorText={String(errors.title)} disableMargin={true} />
										) : null}
									</div>
								</div>
								<div className={styles.fullRow}>
									<label className={styles.label}>Description*</label>
									<Field name="description">
										{({ field, form }: any) => (
											<Editor
												fieldValue={field.value || ''}
												onChange={value => form.setFieldValue(field.name, value)}
												onBlur={() => form.setFieldTouched(field.name, true)}
												placeholder="Describe post"
												isShowToolbar={false}
												isOpportunity={true}
											/>
										)}
									</Field>
									{(touched.description || submitCount > 0) && errors.description ? (
										<InputErrorHint errorText={String(errors.description)} disableMargin={true} />
									) : null}
								</div>
								<div className={styles.fullRow}>
									<label className={styles.label}>Expiration Date</label>
									<Field name="expirationDate">
										{({ field }: any) => (
											<InlineDatePicker
												fieldName={field.name}
												fieldValue={field.value}
												setFieldValue={setFieldValue}
												availableFuture={true}
												cleareble={true}
											/>
										)}
									</Field>
								</div>
								<div className={styles.fullRow}>
									<label className={styles.label}>Location*</label>
									<div className={styles.switches}>
										<ButtonOutlined
											parentClassName={cx(styles.switchButton, {
												[styles.switchButtonActive]: !values.isVirtual,
											})}
											title="Local"
											clickHandler={() => setFieldValue('isVirtual', false)}
										/>
										<ButtonOutlined
											parentClassName={cx(styles.switchButton, {
												[styles.switchButtonActive]: values.isVirtual,
											})}
											title="Virtual"
											clickHandler={() => {
												setFieldValue('isVirtual', true);
												setFieldValue('address', undefined);
											}}
										/>
									</div>
									{!values.isVirtual ? (
										<Field name="address">
											{({ field }: any) => (
												<GoogleApiAutoCompleat
													fieldName={field.name}
													fieldValue={field.value}
													selectHandler={addr => setFieldValue(field.name, addr)}
													setFieldTouched={setFieldTouched as any}
													setFieldValue={setFieldValue as any}
													placeholder="Location"
												/>
											)}
										</Field>
									) : null}
									{!values.isVirtual && (touched.address || submitCount > 0) && errors.address ? (
										<InputErrorHint errorText="Location is required" disableMargin={true} />
									) : null}
								</div>
							</div>
							<div className={styles.actions}>
								<ButtonDefault classList={['secondary', 'lg']} title="Cancel" clickHandler={onClose} />
								<ButtonDefault
									classList={['primary', 'lg']}
									title="Save"
									submitType={true}
									disabled={isSaving}
								/>
							</div>
							{isSaving ? (
								<div className={styles.loader}>
									<Loader />
								</div>
							) : null}
						</Form>
					)}
				</Formik>
			</section>
		</DialogStyled>
	);
};

export default PostUpsertPopup;
